/**
 * 睡眠の前後の文脈（寝る前の過ごし方・睡眠不足・次の睡眠の目安）
 */

import { Dumbbell, Footprints, Gauge, MapPin, Timer } from 'lucide-react'
import type { ReactNode } from 'react'

import type { SleepDayContext, SleepMetrics } from '@/api/types/sleep'

import { formatJstTime, formatMinutes } from '../format'

type Props = {
  metrics: SleepMetrics
  usualPlaceName: string | null
}

const Row = ({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) => (
  <div className="flex gap-3 py-2">
    <div className="mt-0.5 text-muted-foreground">{icon}</div>
    <div className="min-w-0 flex-1">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <div className="text-sm">{children}</div>
    </div>
  </div>
)

export const SleepBudgetCard = ({ context, totalMin }: { context: SleepDayContext; totalMin: number }) => (
  <section className="rounded-xl border bg-card p-4 shadow-sm">
    <p className="mb-3 text-sm font-medium">睡眠の貯金</p>
    <div className="grid grid-cols-3 gap-2 text-center">
      <div>
        <p className="text-[11px] text-muted-foreground">この日</p>
        <p className="font-semibold tabular-nums">{formatMinutes(totalMin)}</p>
      </div>
      <div>
        <p className="text-[11px] text-muted-foreground">7日平均/日</p>
        <p className="font-semibold tabular-nums">{formatMinutes(context.sleep_7d_avg_min)}</p>
      </div>
      <div>
        <p className="text-[11px] text-muted-foreground">7日の不足</p>
        <p className="font-semibold tabular-nums text-rose-600 dark:text-rose-400">
          {context.debt_7d_min > 0 ? formatMinutes(context.debt_7d_min) : 'なし'}
        </p>
      </div>
    </div>
    <div className="mt-3 rounded-lg bg-indigo-500/10 p-3 text-sm">
      次の睡眠の目安: <span className="font-semibold">{formatMinutes(context.suggested_next_sleep_min)}</span>
      {context.suggested_bedtime && context.suggested_wake && (
        <span className="text-muted-foreground">
          （{context.suggested_bedtime}〜{context.suggested_wake}）
        </span>
      )}
      <p className="mt-1 text-[11px] text-muted-foreground">
        必要量 {formatMinutes(context.need_min)} に不足の一部を上乗せ。時刻はいつもの起床時刻から逆算
      </p>
    </div>
  </section>
)

/** その睡眠の前（前回の起床から就寝まで、最大 24 時間）の過ごし方 */
export const PreSleepDetails = ({ metrics, usualPlaceName }: Props) => (
  <div className="divide-y">
    <Row icon={<Timer className="size-4" />} label="起きていた時間">
      {metrics.awake_hours_before !== null ? `${metrics.awake_hours_before.toFixed(1)}時間` : '--'}
    </Row>
    <Row icon={<Dumbbell className="size-4" />} label="運動（就寝前24時間）">
      {metrics.exercises.length === 0 ? (
        'なし'
      ) : (
        <ul className="space-y-0.5">
          {metrics.exercises.map((x) => (
            <li key={`${x.start}-${x.name}`}>
              {formatJstTime(x.start)} {x.name}（{formatMinutes(x.minutes)}）
              <span className="text-muted-foreground"> 就寝{x.hours_before_sleep.toFixed(1)}時間前</span>
            </li>
          ))}
        </ul>
      )}
    </Row>
    <Row icon={<Gauge className="size-4" />} label="ストレス">
      {metrics.day_stress_avg !== null ? `平均 ${metrics.day_stress_avg.toFixed(0)}` : '--'}
      {metrics.day_high_stress_min !== null && metrics.day_high_stress_min > 0 && (
        <span>・高ストレス {formatMinutes(metrics.day_high_stress_min)}</span>
      )}
      {metrics.stress_before_bed !== null && (
        <span className="text-muted-foreground">（就寝前2時間 {metrics.stress_before_bed.toFixed(0)}）</span>
      )}
    </Row>
    <Row icon={<Footprints className="size-4" />} label="歩数">
      {metrics.day_steps !== null ? `${metrics.day_steps.toLocaleString()}歩` : '--'}
    </Row>
    <Row icon={<MapPin className="size-4" />} label="寝た場所">
      {metrics.place_name ?? '不明'}
      {usualPlaceName && metrics.place_name && metrics.place_name !== usualPlaceName && (
        <span className="ml-1 rounded bg-amber-500/15 px-1 text-[11px] text-amber-600">いつもと違う場所</span>
      )}
      {metrics.trip_title && <p className="text-[11px] text-muted-foreground">旅行中: {metrics.trip_title}</p>}
    </Row>
  </div>
)
